/**
 * useActionPanelState — derives the Action Panel context from app state.
 *
 * Three mutually exclusive states:
 *   - `no-selection`: default — show Add + Search
 *   - `tasks-selected`: one or more tasks selected — show bulk actions
 *   - `task-open`: task detail open — show single-task actions
 *
 * Also exposes derived visibility flags: hidden during drag, on settings/auth.
 */

import { useSearchParams } from "react-router-dom"
import { useViewContext } from "@/app/stores/viewContext"
import type { ViewId } from "@/features/views/viewRegistry"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ActionPanelContext = "no-selection" | "tasks-selected" | "task-open"

export interface ActionPanelState {
  /** Current context state */
  context: ActionPanelContext
  /** Task IDs for bulk operations (non-empty in tasks-selected) */
  selectedTaskIds: string[]
  /** Single task ID when detail is open */
  openTaskId: string | null
  /** Current view type */
  viewId: ViewId
  /** Container ID (project/list/area ID) */
  containerId: string | null
  /** Whether the panel should be visible */
  visible: boolean
  /** Whether the panel should be hidden (drag, settings, auth) */
  hidden: boolean
  /** Whether task creation is allowed in current view */
  allowCreate: boolean
  /** Whether the view is the trash (changes delete → restore) */
  isTrash: boolean
  /** Whether the view is the logbook */
  isLogbook: boolean
}

// Views where the action panel should be completely hidden
const HIDDEN_VIEWS = new Set<string>(["search"])

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useActionPanelState(): ActionPanelState {
  const viewId = useViewContext((s) => s.viewId)
  const containerId = useViewContext((s) => s.containerId)
  const selectedTaskIds = useViewContext((s) => s.selectedTaskIds)
  const allowCreate = useViewContext((s) => s.allowCreate)

  // Read activeTaskId from URL (source of truth for task detail open state)
  const [searchParams] = useSearchParams()
  const activeTaskId = searchParams.get("tid") ?? null

  // Derive context
  let context: ActionPanelContext = "no-selection"
  if (selectedTaskIds.length > 0) {
    context = "tasks-selected"
  } else if (activeTaskId != null) {
    context = "task-open"
  }

  const isTrash = viewId === "trash"
  const isLogbook = viewId === "logbook"

  // Hidden during drag (mobile only) or on certain views
  const hidden = HIDDEN_VIEWS.has(viewId)

  // Visible when not hidden
  const visible = !hidden

  return {
    context,
    selectedTaskIds,
    openTaskId: activeTaskId,
    viewId,
    containerId,
    visible,
    hidden,
    allowCreate,
    isTrash,
    isLogbook,
  }
}
